import ReduxStore from 'src/types/store/reduxStore';
import { Entity, Conditions, RangeFilter } from 'sl-api-connector/types';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { buildFullMonthRangeFilters } from 'src/utils/dateformatting';
import { buildAggregations } from 'src/components/AdManager/Search';
import { Option } from 'funfix-core';
import { store } from 'src/main';
import { fetchEntityMetrics } from 'src/store/modules/entitySearchService/operations';
import mitt from 'mitt';
import { getSearchAppName } from 'src/utils/app';
import { buildMainEntityConditions } from 'src/components/EntityPage/Renderer/EntityPageRenderer';
import { AggregationField } from 'src/types/application/types';
import _get from 'lodash/get';

export const emitter = mitt();

export const fetchDailySpendAndProjections = (
  baseDataKey: string,
  { app, retailer, mainEntity }: Pick<ReduxStore, 'app' | 'retailer'> & { mainEntity: Entity },
  conditions: Conditions
) => {
  const fetchData = (dataKey: string, indexName: string, fields: string[], isProjected: number) => {
    const timePeriodRangeFilters: RangeFilter[] = buildFullMonthRangeFilters();
    const metricFields = fields.map((fieldName) =>
      INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, 'campaign', 'dayId')
    );
    const groupByField = INDEX_FIELDS.getField(getSearchAppName(), indexName, 'dayId');
    let useAggregationSort = true;
    const baseKey = baseDataKey.split('-')[0];

    // We only want to turn off aggregation based sort for the donutChart & dailyAdSpendBarChart
    if (['scoreDonutChart', 'dailyAdSpendBarChart'].includes(baseKey)) {
      useAggregationSort = false;
    }

    const [{ aggregations: aggregationFields }] = buildAggregations(metricFields);
    const queryConditions = {
      // Strip out `categoryId` conditions since they don't work with `dayId` aggregations on these special indices
      termFilters: Option.of(conditions.termFilters)
        .map((termFilters) => termFilters.filter(({ fieldName }) => fieldName !== 'categoryId'))
        .getOrElse([
          {
            fieldName: 'isProjected',
            condition: 'must',
            values: [isProjected]
          }
        ]),
      rangeFilters: timePeriodRangeFilters
    };
    if (queryConditions.termFilters.findIndex((x) => x.fieldName === 'isProjected') === -1) {
      queryConditions.termFilters.push({
        fieldName: 'isProjected',
        condition: 'must',
        values: [isProjected]
      });
    }

    store.dispatch(
      fetchEntityMetrics(
        dataKey,
        {
          entity: mainEntity,
          retailer,
          app: { ...app, name: getSearchAppName(), apiAppName: getSearchAppName() },
          indexName
        },
        [
          {
            doAggregation: true,
            aggregations: [
              {
                aggregationFields,
                conditions: {
                  termFilters: [{ fieldName: 'retailerId', values: [+retailer.id] }],
                  rangeFilters: timePeriodRangeFilters
                },
                groupByFieldName: groupByField.name
              }
            ],
            conditions: queryConditions,
            shouldCache: false,
            useAggregationSort,
            pageSize: 100
          }
        ]
      )
    );
  };

  // Fetch projections to use when no real data is available
  fetchData(`${baseDataKey}-projections`, 'adCampaignDailyMetrics', ['spend', 'budgetAmount', 'isProjected'], 1);
  fetchData(`${baseDataKey}-actual`, 'adCampaignDailyMetrics', ['spend', 'budgetAmount', 'isProjected'], 0);
};

export const INELIGIBLE_FIELDS = [
  'ad_policing_suspended',
  'ineligible',
  'missing_decoration',
  'missing_image',
  'not_buyable',
  'not_in_buybox'
];

const ineligibleParser = (action: any): DefaultInfo => {
  const { groupByFieldName: groupByField } = action.apiRequest[0].aggregations[0];
  const productSkus: any[] = [];
  const allStacklineSkus = new Set();
  const result = action.apiRequest.map((request: any, index: number) => {
    const data = action.apiResponse.data[index];
    const dataSet = data.aggregations[`by_${groupByField}`] || [];
    const stacklineSkus = dataSet.map((dataPoint: any) =>
      _get(dataPoint, ['additionalMetaData', 'product', 'stacklineSku'])
    );
    stacklineSkus.forEach((sku: string) => {
      allStacklineSkus.add(sku);
    });
    productSkus.push(...stacklineSkus);
    return {
      count: stacklineSkus.length,
      stacklineSkus
    };
  });
  const totalCount = allStacklineSkus.size;
  return {
    totalCount,
    productSkus,
    adPolicingSuspended: result[0],
    ineligible: result[1],
    missingDecoration: result[2],
    missingImage: result[3],
    notBuyable: result[4],
    missingBuyBox: result[5]
  };
};

interface DefaultInfo {
  [key: string]: any;
  productSkus: any[];
  totalCount: number;
  ineligible: any;
  missingDecoration: any;
  notBuyable: any;
  missingBuyBox: any;
}

export const fetchIneligibleCountForEntity = async (entity: Entity): Promise<DefaultInfo> => {
  const { app, retailer } = store.getState();
  const dataKey = `ineligibleCount-${entity.type}-${entity.id}`;

  const indexName = 'adCampaignAdGroupProduct';
  const entityConditions = buildMainEntityConditions({ termFilters: [], rangeFilters: [] }, entity, app, retailer);

  const fields: AggregationField[] = ['stacklineSku'].map((fieldName) =>
    INDEX_FIELDS.getField(app.name, indexName, fieldName)
  );

  const [{ aggregations: aggregationFields }] = buildAggregations(fields);

  const searchRequestOverrides = INELIGIBLE_FIELDS.map((statusStr) => {
    return {
      doAggregation: true,
      aggregations: [
        {
          aggregationFields,
          conditions: {
            termFilters: [{ fieldName: 'retailerId', values: [Number.parseInt(retailer.id as any, 10)] }],
            rangeFilters: []
          },
          groupByFieldName: 'stacklineSku'
        }
      ],
      conditions: {
        termFilters: [
          ...entityConditions.termFilters,
          {
            fieldName: 'status',
            condition: 'must_not',
            values: ['ended', 'archived']
          },
          {
            fieldName: 'statusReason',
            condition: 'should',
            values: [statusStr]
          }
        ]
      },
      pageNumber: 1,
      pageSize: 1000,
      searchBy: 'parent',
      processDocuments: false,
      useAggregationSort: false
    };
  });

  return new Promise((resolve) => {
    store.dispatch(
      fetchEntityMetrics(
        dataKey,
        {
          entity,
          retailer,
          app,
          indexName,
          customResponseParser: (action: any) => {
            const result = ineligibleParser(action);
            resolve(result);
            return result;
          }
        },
        searchRequestOverrides,
        null,
        true
      )
    );
  }) as Promise<DefaultInfo>;
};
